/**
 * Leitura dos pixels da imagem do mapa para achar a grade desenhada nela (a conta fica em
 * `mapGrid.ts`). Duas passadas: a detecção numa cópia reduzida (rápida, e a redução
 * amacia os blocos da compressão JPEG) e a medida fina na resolução cheia, só perto do
 * tamanho achado. Na medida fina, o giro da grade desenhada é medido e a imagem é
 * desgirada no canvas antes de medir de novo (até o giro que sobra ser desprezível). Os
 * resultados voltam em px da imagem como ela aparece na cena (girada pelo `angle`).
 *
 * Imagem de outro site: o navegador só deixa ler os pixels com permissão (CORS), que a
 * maioria dos sites de mapas não dá; então ela vem pelo servidor do RPG Desk
 * (`/api/image-fetch`, só endereço público). Se nem assim der, não há detecção e o encaixe
 * fica por conta do tamanho da imagem.
 */
import { useApp } from '../store.js';
import { LINE_KINDS, detectImageGrid, measureRotation, snapToImageLines, type ImageGrid } from './mapGrid.js';
import { isServerOrigin, serverUrl } from '../serverUrl.js';

/** Cópia para detectar: lado maior até 1600 px e no máximo 80% do original. */
const DETECT_SIDE = 1600;
const DETECT_SCALE = 0.8;
/** Medida fina: resolução cheia até 4096 px no lado maior. */
const MEASURE_SIDE = 4096;
/** Na medida fina, o tamanho da célula muda no máximo isto em relação ao achado. */
const MEASURE_RANGE = 0.03;
/** Giro menor que isto (graus) é ruído de medida: a imagem fica reta. */
const MIN_ANGLE = 0.02;
/** Voltas de desgirar e medir de novo; para quando o que sobra é menor que isto (graus). */
const ANGLE_PASSES = 3;
const ANGLE_SETTLED = 0.01;

/** Grade da imagem (px dela, girada por `angle` graus, como aparece na cena). */
export type MeasuredGrid = { grid: ImageGrid; angle: number };

type Pixels = { gray: Float32Array; width: number; height: number; scale: number };

/**
 * De onde ler os pixels: o próprio endereço (mesmo site), uma cópia do bucket (arquivo do
 * RPG Desk) ou a cópia que o servidor buscou (outro site).
 *
 * O link de um arquivo do RPG Desk redireciona para o bucket, que é outro site e libera a leitura
 * por CORS. A cópia é buscada à parte e sem cache: a que o `<img>` da cena guardou veio sem os
 * cabeçalhos de CORS, e o navegador recusaria reaproveitá-la.
 */
async function readableSource(url: string, timeoutMs: number): Promise<{ src: string; done: () => void } | null> {
  // o servidor pode estar noutro endereço que a página (src/serverUrl.ts): ele também libera CORS
  const target = new URL(serverUrl(url), window.location.href);
  if (isServerOrigin(target) && target.pathname.startsWith('/api/files/')) return blobSource(target.href, {}, timeoutMs, 'no-store');
  if (target.origin === window.location.origin) return { src: target.href, done: () => {} };
  const { token } = useApp.getState();
  if (!token) return null;
  return blobSource(serverUrl(`/api/image-fetch?url=${encodeURIComponent(target.href)}`), { authorization: `Bearer ${token}` }, timeoutMs);
}

/** Baixa a imagem e devolve um endereço local (blob:) para ela, ou null se não veio. */
async function blobSource(url: string, headers: Record<string, string>, timeoutMs: number, cache?: RequestCache): Promise<{ src: string; done: () => void } | null> {
  const abort = new AbortController();
  const timer = window.setTimeout(() => abort.abort(), timeoutMs);
  try {
    const response = await fetch(url, { headers, signal: abort.signal, ...(cache ? { cache } : {}) });
    if (!response.ok) return null;
    const src = URL.createObjectURL(await response.blob());
    return { src, done: () => URL.revokeObjectURL(src) };
  } finally {
    window.clearTimeout(timer);
  }
}

function loadImage(url: string, timeoutMs: number): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const timer = window.setTimeout(() => {
      img.src = '';
      reject(new Error('timeout'));
    }, timeoutMs);
    img.onload = () => {
      window.clearTimeout(timer);
      resolve(img);
    };
    img.onerror = () => {
      window.clearTimeout(timer);
      reject(new Error('load'));
    };
    img.src = url;
  });
}

/** A imagem carregada (uma vez), ou null quando não dá para ler os pixels. */
async function openImage(url: string, timeoutMs = 20_000): Promise<{ img: HTMLImageElement; done: () => void } | null> {
  let source: Awaited<ReturnType<typeof readableSource>> = null;
  try {
    source = await readableSource(url, timeoutMs);
    if (!source) return null;
    const img = await loadImage(source.src, timeoutMs);
    if (!img.naturalWidth || !img.naturalHeight) throw new Error('vazia');
    return { img, done: source.done };
  } catch {
    // imagem quebrada, demorada demais ou que o servidor não pôde buscar
    source?.done();
    return null;
  }
}

/**
 * Tons de cinza da imagem com o lado maior até `maxSide` px (e no máximo `maxScale` do
 * original), girada `angle` graus em volta do centro (como a cena desenha).
 */
function grayAt(img: HTMLImageElement, maxSide: number, maxScale: number, angle = 0): Pixels | null {
  const scale = Math.min(maxScale, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
  const width = Math.max(1, Math.round(img.naturalWidth * scale));
  const height = Math.max(1, Math.round(img.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  // redução de boa qualidade: média da área, não amostra de um pixel (que inventa padrões)
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  try {
    if (angle) {
      ctx.translate(width / 2, height / 2);
      ctx.rotate((angle * Math.PI) / 180);
      ctx.drawImage(img, -width / 2, -height / 2, width, height);
    } else ctx.drawImage(img, 0, 0, width, height);
    const rgba = ctx.getImageData(0, 0, width, height).data;
    const gray = new Float32Array(width * height);
    for (let i = 0; i < gray.length; i += 1) {
      const o = i * 4;
      // transparente conta como escuro (as bordas de um recorte também marcam)
      const a = rgba[o + 3]! / 255;
      gray[i] = (0.299 * rgba[o]! + 0.587 * rgba[o + 1]! + 0.114 * rgba[o + 2]!) * a;
    }
    // a redução pode não ser exatamente proporcional nos dois sentidos
    return { gray, width, height, scale: width / img.naturalWidth };
  } catch {
    return null;
  }
}

const scaleGrid = (grid: ImageGrid, k: number): ImageGrid => ({ cellW: grid.cellW * k, cellH: grid.cellH * k, phaseX: grid.phaseX * k, phaseY: grid.phaseY * k });

/** A grade desenhada na imagem e o giro que a endireita, ou null quando não há uma clara ou não dá para ler. */
export async function measureImageGrid(url: string): Promise<MeasuredGrid | null> {
  const opened = await openImage(url);
  if (!opened) return null;
  try {
    const small = grayAt(opened.img, DETECT_SIDE, DETECT_SCALE);
    if (!small) return null;
    // células de menos de 12 px da imagem original não são grade de mapa
    const found = detectImageGrid(small.gray, small.width, small.height, Math.max(6, 12 * small.scale));
    if (!found) return null;
    const rough = scaleGrid(found, 1 / small.scale);
    // medida fina na resolução cheia, no mesmo tipo de linha
    const full = grayAt(opened.img, MEASURE_SIDE, 1);
    if (!full) return { grid: rough, angle: 0 };
    let grid = scaleGrid(rough, full.scale);
    // giro da grade desenhada; a imagem gira o contrário
    let tilt = measureRotation(full.gray, full.width, full.height, grid, found.kind);
    if (Math.abs(tilt) < MIN_ANGLE) tilt = 0;
    for (let pass = 0; pass < ANGLE_PASSES; pass += 1) {
      const view = tilt ? grayAt(opened.img, MEASURE_SIDE, 1, -tilt) : full;
      if (!view) break;
      grid = snapToImageLines(grid, view.gray, view.width, view.height, [found.kind], MEASURE_RANGE);
      if (!tilt) break;
      const rest = measureRotation(view.gray, view.width, view.height, grid, found.kind);
      if (Math.abs(rest) < ANGLE_SETTLED) break;
      tilt += rest;
    }
    return { grid: scaleGrid(grid, 1 / full.scale), angle: tilt ? -tilt : 0 };
  } finally {
    opened.done();
  }
}

/**
 * Acerta pelas linhas da imagem a grade que as marcas descrevem (px da imagem girada por
 * `angle`, como as marcas foram feitas na cena).
 */
export async function snapMarksToImage(url: string, approx: ImageGrid, angle = 0): Promise<ImageGrid> {
  const opened = await openImage(url);
  if (!opened) return approx;
  try {
    const full = grayAt(opened.img, MEASURE_SIDE, 1, angle);
    if (!full) return approx;
    return scaleGrid(snapToImageLines(scaleGrid(approx, full.scale), full.gray, full.width, full.height, LINE_KINDS), 1 / full.scale);
  } finally {
    opened.done();
  }
}
